import { useState } from 'react';
import Image from 'next/image';
import { useGlobalContext } from '@/context/GlobalContext';
import sanitizeHTML from '@/utils/sanatizeHtml';

import Accordion from '@/components/ui/components/Accordion';

import ProgramsComponent from './Program';
import TabularComponent from './TabularComponent';

const PageData = ({ tabData, pageData, id }: any) => {
  const { setIsLeadFormOpen, setLeadFormData } = useGlobalContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [selectedGalleryImages, setSelectedGalleryImages] = useState<any[]>([]);
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);
  const [openChildAccordions, setOpenChildAccordions] = useState<Record<string, boolean>>({});

  const handleAccordionToggle = (id: string) => {
    setOpenAccordionId((prev) => (prev === id ? null : id));
  };

  const handleChildAccordionToggle = (id: string) => {
    setOpenChildAccordions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleOpen = (course: any) => {
    const courseData: any = [
      {
        id: course?.id,
        abbreviation: course?.abbreviation,
        course_name: course?.course_name,
      },
    ];
    setLeadFormData({
      source: `collegePage`,
      course: courseData ?? null,
      title: course?.course_name ?? null,
    });
    setIsLeadFormOpen(true);
  };

  const openModal = (imageUrl: string, galleryImages: any[]) => {
    setSelectedImage(imageUrl);
    setSelectedGalleryImages(galleryImages);
    setIsModalOpen(true);
  };

  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const filteredCourses = pageData?.Courses?.filter((course: any) => {
    const courseName = course?.course_name?.course_name.toLowerCase();
    return courseName?.includes(searchTerm.toLowerCase());
  });

  if (
    tabData?.__component === 'common.latest-update' ||
    tabData?.__component === 'course.latest-update' ||
    tabData?.__component === 'special.latest-update' ||
    tabData?.__component === 'exam.latest-update'
  ) {
    return (
      <section>
        <div className="my-2 flex gap-2 lg:mx-auto lg:mt-4 lg:max-w-screen-xl">
          <div className="flex flex-1 flex-col gap-2">
            <Accordion
              title={tabData?.heading}
              //id={tabData?.id}
              titlePrimary
              opened={openAccordionId === tabData?.id}
              isBgColor
              onToggle={() => handleAccordionToggle(tabData?.id)}
            >
              <div className="flex flex-col gap-1" id={tabData?.id}>
                {tabData?.sub_heading && (
                  <div className="bg-orange-100 p-2 text-lg text-gray-600 md:pl-4">
                    <p className="text-sm md:text-base">{tabData?.sub_heading}</p>
                  </div>
                )}
                {tabData?.Content?.trim() &&
                  tabData?.Content?.split('<br>')?.filter((line: string) => {
                    const cleanedLine = line?.trim()?.replace(/<\/?(div|p)[^>]*>/g, '');
                    return cleanedLine !== '' && cleanedLine !== '&nbsp;';
                  })?.length > 0 && (
                    <div className="ckeditorData flex flex-col !overflow-x-auto text-wrap bg-orange-100 p-2 text-start font-poppins text-sm leading-tight md:text-base">
                      {tabData?.Content?.split('<br>')
                        ?.filter((line: string) => {
                          const cleanedLine = line?.trim()?.replace(/<\/?(div|p)[^>]*>/g, '');
                          return cleanedLine !== '' && cleanedLine !== '&nbsp;';
                        })
                        ?.map((line: any, index: number) => {
                          const sanitizedLine = sanitizeHTML(line?.trim()?.replace(/<\/?(div|p)[^>]*>/g, ''));
                          return (
                            <div key={index} className="flex items-start gap-2 py-0.5 md:items-center">
                              <div
                                className="inline-block text-left"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizedLine,
                                }}
                              ></div>
                            </div>
                          );
                        })}
                    </div>
                  )}
              </div>
            </Accordion>
          </div>
        </div>
      </section>
    );
  } else if (
    tabData?.__component === 'common.new-overview' ||
    tabData?.__component === 'course.overview' ||
    tabData?.__component === 'special.overview' ||
    tabData?.__component === 'exam.overview'
  ) {
    // Extract first <h2> from content
    let contentHtml = tabData?.content ?? tabData?.Content ?? '';
    let overviewHeading = tabData?.heading ?? '';
    let headingTag = 'p';

    if (typeof window !== 'undefined') {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(contentHtml, 'text/html');
      const headingEl = doc.querySelector('h2,h3,h4,h5,h6');
      if (headingEl) {
        headingTag = headingEl.tagName.toLowerCase(); // e.g., 'h3'
        overviewHeading = headingEl.textContent ?? overviewHeading;
        headingEl.remove();
        contentHtml = doc.body.innerHTML;
      }
    }

    console.log('overview heading', overviewHeading);

    return (
      <section>
        <div className="flex gap-4 lg:mx-auto lg:max-w-screen-xl">
          <div className="flex flex-1 flex-col gap-4">
            <Accordion
              title={overviewHeading}
              titleTag={headingTag}
              titlePrimary
              opened={openAccordionId === tabData?.id}
              onToggle={() => handleAccordionToggle(tabData?.id)}
            >
              <div className="flex flex-col gap-5" id={tabData?.id}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(contentHtml),
                  }}
                  className="ckeditorData !overflow-x-auto text-wrap font-inter text-sm md:text-base"
                />
              </div>
            </Accordion>
          </div>
        </div>
      </section>
    );
  } else if (
    tabData?.__component == 'common.faq' ||
    tabData?.__component == 'course.faq' ||
    tabData?.__component == 'course.faq' ||
    tabData?.__component == 'exam.faq'
  ) {
    return (
      <section className="mt-3 rounded-lg bg-white px-2 shadow-lg lg:px-4">
        <h5 className=" text-base font-bold text-primary md:text-xl">{tabData?.heading}</h5>
        <div className="mt-5 flex flex-col" id={id}>
          {tabData?.questions?.length > 0 ? (
            tabData?.questions.map((question: any, index: number) => {
              const childId = `${tabData?.id}-${index}`;
              return (
                <div key={childId} className="my-2 cursor-pointer border-b border-b-gray-300 pb-4 font-inter">
                  <div
                    role="none"
                    className="flex cursor-pointer items-center justify-between gap-x-1.5 text-base"
                    onClick={() => handleChildAccordionToggle(childId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleChildAccordionToggle(childId);
                      }
                    }}
                  >
                    <div className="text-sm font-semibold md:text-base">
                      <span className="mr-2 inline-block text-sm font-semibold text-primary-text">{index + 1}.</span>
                      {question?.Question}
                    </div>
                    {/* <div>
                      <ChevrondownSvg
                        className={`text-gray-600 duration-200 sm:text-lg ${
                          openChildAccordions[childId] ? "" : "-rotate-90"
                        }`}
                      />
                    </div> */}
                  </div>
                  <div
                    className={`overflow-hidden pr-4 transition-all duration-300 ${
                      openChildAccordions[childId] ? 'max-h-60' : 'max-h-0'
                    }`}
                  >
                    <p className="my-2 ml-6 cursor-default text-xs font-medium leading-normal tracking-wide text-primary-gray md:text-sm">
                      {question?.Answer}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <p>No FAQs available for this category.</p>
          )}
        </div>
      </section>
    );
  } else if (tabData?.__component == 'common.gallery') {
    return (
      <>
        <Accordion
          title={tabData?.heading}
          //id={tabData?.id}
          titlePrimary
          opened={openAccordionId === tabData?.id}
          onToggle={() => handleAccordionToggle(tabData?.id)}
        >
          <div className="flex flex-col gap-5" id={id}>
            <div className=" grid grid-flow-row gap-4">
              {tabData.Gallery_Type?.map((gallery: any, index: number) => {
                const galleryId = `${gallery?.id}-${index}`;
                return (
                  <div key={galleryId}>
                    <h2 className="my-1 text-base text-primary md:my-2 md:text-2xl">{gallery?.Heading}</h2>
                    <div className="grid grid-cols-3 gap-2">
                      {gallery?.gallery?.length > 0 &&
                        gallery?.gallery.map((image: any, ind: any) => {
                          const galleryImageId = `${image?.id}-${ind}`;
                          return (
                            <Image
                              key={galleryImageId}
                              src={image?.url}
                              width={500}
                              height={500}
                              className="h-16 w-full cursor-pointer object-cover duration-200 ease-in-out hover:border-2 hover:border-blue-500 hover:p-1 hover:opacity-75 md:h-44"
                              onClick={() => openModal(image?.url, gallery?.gallery)}
                              alt={image?.name ?? 'banner_image'}
                            />
                          );
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Accordion>
        {/* <ImageModal
          isOpen={isModalOpen}
          onClose={closeModal}
          selectedImage={selectedImage}
          images={selectedGalleryImages}
          setSelectedImage={setSelectedImage}
        /> */}
      </>
    );
  } else if (
    tabData?.__component == 'common.course-component' ||
    tabData?.__component == 'course.course-component' ||
    tabData?.__component == 'special.course-component' ||
    tabData?.__component == 'exam.course-component'
  ) {
    return (
      <Accordion
        title={tabData?.heading}
        //id={tabData?.id}
        titlePrimary
        opened={openAccordionId === tabData?.id}
        onToggle={() => handleAccordionToggle(tabData?.id)}
      >
        {tabData?.is_tabular_view ? (
          <TabularComponent data={filteredCourses} />
        ) : (
          <div className="flex flex-col gap-4 text-sm md:p-2">
            <div className="relative w-full">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                {/* <CiSearch className="text-gray-400" /> */}
              </span>
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 p-3 pl-10 outline-gray-300"
                placeholder="Search"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            {filteredCourses?.length > 0 ? (
              <div className="hide-scrollbar flex w-full gap-4 overflow-x-auto md:grid md:grid-cols-3">
                {filteredCourses.map((course: any, index: any) => {
                  return (
                    <ProgramsComponent
                      course={course}
                      key={`${course?.id}-${index}`}
                      onApplyNow={() => {
                        return handleOpen(course?.course_name);
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="mt-4 text-center text-gray-500">No search results found</div>
            )}
          </div>
        )}
      </Accordion>
    );
  }
};

export default PageData;
